import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import schoolYearService from "./schoolYearService";

/**
 * GET SCHOOL YEARS
 */
export const getSchoolYears = createAsyncThunk(
  "schoolYears/getAll",
  async (_, thunkAPI) => {
    try {
      return await schoolYearService.getSchoolYears();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

/**
 * GET ACTIVE SCHOOL YEAR
 */
export const getActiveSchoolYear = createAsyncThunk(
  "schoolYears/getActive",
  async (_, thunkAPI) => {
    try {
      return await schoolYearService.getActiveSchoolYear();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

/**
 * CREATE SCHOOL YEAR
 */
export const createSchoolYear = createAsyncThunk(
  "schoolYears/create",
  async (data, thunkAPI) => {
    try {
      return await schoolYearService.createSchoolYear(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

/**
 * UPDATE SCHOOL YEAR
 */
export const updateSchoolYear = createAsyncThunk(
  "schoolYears/update",
  async ({ id, data }, thunkAPI) => {
    try {
      return await schoolYearService.updateSchoolYear({
        id,
        data,
      });
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

/**
 * ACTIVATE SCHOOL YEAR
 */
export const activateSchoolYear = createAsyncThunk(
  "schoolYears/activate",
  async (id, thunkAPI) => {
    try {
      return await schoolYearService.activateSchoolYear(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

/**
 * LOCK SCHOOL YEAR
 */
export const lockSchoolYear = createAsyncThunk(
  "schoolYears/lock",
  async (id, thunkAPI) => {
    try {
      return await schoolYearService.lockSchoolYear(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

/**
 * UNLOCK SCHOOL YEAR
 */
export const unlockSchoolYear = createAsyncThunk(
  "schoolYears/unlock",
  async (id, thunkAPI) => {
    try {
      return await schoolYearService.unlockSchoolYear(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

/**
 * DELETE SCHOOL YEAR
 */
export const deleteSchoolYear = createAsyncThunk(
  "schoolYears/delete",
  async (id, thunkAPI) => {
    try {
      return await schoolYearService.deleteSchoolYear(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

const initialState = {
  schoolYears: [],

  activeSchoolYear: null,

  selectedSchoolYear: null,

  isLoading: false,

  isCreating: false,

  isUpdating: false,

  isDeleting: false,

  error: null,
};

const schoolYearSlice = createSlice({
  name: "schoolYears",

  initialState,

  reducers: {
    clearSchoolYearError: (state) => {
      state.error = null;
    },

    setSelectedSchoolYear: (state, action) => {
      state.selectedSchoolYear = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder

      /**
       * GET ALL
       */
      .addCase(getSchoolYears.pending, (state) => {
        state.isLoading = true;

        state.error = null;
      })

      .addCase(getSchoolYears.fulfilled, (state, action) => {
        state.isLoading = false;

        state.schoolYears = action.payload?.data || [];
      })

      .addCase(getSchoolYears.rejected, (state, action) => {
        state.isLoading = false;

        state.error = action.payload;
      })

      /**
       * GET ACTIVE
       */
      .addCase(getActiveSchoolYear.fulfilled, (state, action) => {
        state.activeSchoolYear = action.payload?.data || null;
      })

      /**
       * CREATE
       */
      .addCase(createSchoolYear.pending, (state) => {
        state.isCreating = true;

        state.error = null;
      })

      .addCase(createSchoolYear.fulfilled, (state, action) => {
        state.isCreating = false;

        state.schoolYears.unshift(action.payload.data);
      })

      .addCase(createSchoolYear.rejected, (state, action) => {
        state.isCreating = false;

        state.error = action.payload;
      })

      /**
       * UPDATE
       */
      .addCase(updateSchoolYear.pending, (state) => {
        state.isUpdating = true;

        state.error = null;
      })

      .addCase(updateSchoolYear.fulfilled, (state, action) => {
        state.isUpdating = false;

        state.schoolYears = state.schoolYears.map((item) =>
          item._id === action.payload.data?._id ? action.payload.data : item,
        );
      })

      .addCase(updateSchoolYear.rejected, (state, action) => {
        state.isUpdating = false;

        state.error = action.payload;
      })

      /**
       * ACTIVATE
       */
      .addCase(activateSchoolYear.fulfilled, (state, action) => {
        const updated = action.payload.data;

        state.schoolYears = state.schoolYears.map((item) => ({
          ...item,

          is_active: item._id === updated._id,
        }));

        state.activeSchoolYear = updated;
      })

      /**
       * LOCK
       */
      .addCase(lockSchoolYear.fulfilled, (state, action) => {
        state.schoolYears = state.schoolYears.map((item) =>
          item._id === action.payload.data?._id ? action.payload.data : item,
        );
      })

      /**
       * UNLOCK
       */
      .addCase(unlockSchoolYear.fulfilled, (state, action) => {
        state.schoolYears = state.schoolYears.map((item) =>
          item._id === action.payload.data?._id ? action.payload.data : item,
        );
      })

      /**
       * DELETE
       */
      .addCase(deleteSchoolYear.pending, (state) => {
        state.isDeleting = true;

        state.error = null;
      })

      .addCase(deleteSchoolYear.fulfilled, (state, action) => {
        state.isDeleting = false;

        const deletedId = action.meta.arg;

        state.schoolYears = state.schoolYears.filter(
          (item) => item._id !== deletedId,
        );
      })

      .addCase(deleteSchoolYear.rejected, (state, action) => {
        state.isDeleting = false;

        state.error = action.payload;
      });
  },
});

export const {
  clearSchoolYearError,

  setSelectedSchoolYear,
} = schoolYearSlice.actions;

export default schoolYearSlice.reducer;
